import React, { useState, useRef, useEffect } from 'react';
import { Segment, Icon, Header, Modal, Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { HtmlEditor } from 'devextreme-react/html-editor';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { ErrorSegment, ReportForm, VideoPlayer, TextEditor, SpreadsheetEditor, Calculator, FlexWindow } from '../../..';
import { Consts, Styles } from '../../../../res';
import FreeModal from '../FreeModal';
import { MdFormatStrikethrough } from "react-icons/md";
import { replaceFontFamily } from '../../../../helpers/general';
import TbsPdfReader from '../TbsPdfReader';
import QuestionTextViewer from '../QuestionTextViewer';
import TextEditorTinyMC from '../../../Generals/TextEditorTinyMC';
import SpreadsheetEditorNew from '../../../Generals/SpreadsheetEditorNew';
import './PageDifr.css';

const { noExtraSpaces, f_14_700_text } = Styles;
const sidebarItemTextStyle = { ...f_14_700_text, fontFamily: 'Arial' };

const getAttachmentKey = (index, userQuestionRoundId) => `attachment_${index}_${userQuestionRoundId}`;

const QuestionPage = (props) => {
    const { Q, onAnswerQuestion, userSeeExamQuestions, loading, error, isMockExam, isViewOnly, onFlagQuestion, onFavQuestion, user } = props;
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const [showReportIssue, setShowReportIssue] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showAttachmentVideo, setShowAttachmentVideo] = useState(null);
    const [freeModals, setFreeModals] = useState({});
    const [scratchPad, setScratchPad] = useState('');
    const [highlightColor, setHighlightColor] = useState('yellow');
    const [highlightMode, setHighlightMode] = useState('add'); // 'add' or 'remove'
    const [savedSelection, setSavedSelection] = useState(null);
    const pdfReaderRefs = useRef({});
    const questionTextRef = useRef(null);
    const [activeModalKey, setActiveModalKey] = useState(null);
    const [showCalculator, setShowCalculator] = useState(false);
    const [activeElement, setActiveElement] = useState(null);

    const QRef = useRef(Q);
    const latestAnswerJsonRef = useRef(Q?.answerJson || '{}');
    useEffect(() => {
        QRef.current = Q;
        latestAnswerJsonRef.current = Q?.answerJson || '{}';
    }, [Q]);

    useEffect(() => {
        function handleFocus(event) {
            const target = event.target;

            const isTypable =
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.isContentEditable;

            if (isTypable) {
                setActiveElement(target); // Save the focused element
            }
        }

        document.addEventListener("focus", handleFocus, true);

        return () => {
            document.removeEventListener("focus", handleFocus, true);
        };
    }, []);

    useEffect(() => {
        if (Q && !Q.seen) userSeeExamQuestions({ ...Q, seen: true });
    }, [Q]);

    const addTextToActiveElement = (text) => {
        if (!activeElement) return;

        if (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA") {
            activeElement.value += text;
        } else if (activeElement.isContentEditable) {
            activeElement.textContent += text;
        }

        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(activeElement);
        range.collapse(false); // false means collapse to end
        sel.removeAllRanges();
        sel.addRange(range);
        activeElement.focus();
    };

    const bringToFront = (key) => {
        setFreeModals(prev => {
            const maxZ = Math.max(10, ...Object.values(prev).filter(v => typeof v === 'number'));
            return { ...prev, [key]: maxZ + 1 };
        });
        setActiveModalKey(key);
    };

    const getActivePdfRef = () => {
        if (activeModalKey && activeModalKey.startsWith('attachment_')) {
            return pdfReaderRefs.current[activeModalKey];
        }
        return null;
    };

    const handleOpenFreeModal = (key) => {
        bringToFront(key);
    };

    // Save current selection before any UI interaction
    const saveSelection = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            setSavedSelection(range.cloneRange());
            console.log('Selection saved');
        }
    };

    // Restore selection after UI interaction
    const restoreSelection = () => {
        if (savedSelection) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedSelection);
            console.log('Selection restored');
        }
    };

    const handleHighlightPdf = (color = null) => {
        const selectedColor = color || highlightColor;
        const mode = color ? 'add' : highlightMode; // If color is passed, it's always add mode

        // Save selection before processing
        saveSelection();

        // If mode is remove, call remove function instead
        if (mode === 'remove') {
            // Try question text first if it has a selection
            if (questionTextRef.current && questionTextRef.current.hasSelection()) {
                questionTextRef.current.removeHighlightFromSelection();
                // Don't restore selection after removal - let it stay cleared
                return;
            }

            const pdfRef = getActivePdfRef();
            if (pdfRef && pdfRef.hasSelection()) {
                pdfRef.removeHighlightFromSelection();
                // Don't restore selection after removal - let it stay cleared
                return;
            }

            // If no selection found, try question text anyway
            if (questionTextRef.current) {
                questionTextRef.current.removeHighlightFromSelection();
                // Don't restore selection after removal - let it stay cleared
            }
            return;
        }

        // Try question text first if it has a selection
        if (questionTextRef.current && questionTextRef.current.hasSelection()) {
            console.log('Question text has selection, applying highlight');
            questionTextRef.current.triggerHighlight(selectedColor);
            // Don't restore selection after highlighting
            return;
        }

        // Find the active PDF reader (the one with the highest z-index/most recent modal)
        const pdfRef = getActivePdfRef();
        if (pdfRef && pdfRef.hasSelection()) {
            pdfRef.triggerHighlight(selectedColor);
            // Don't restore selection after highlighting
            return;
        }

        // If no selection found, try question text anyway
        if (questionTextRef.current) {
            questionTextRef.current.triggerHighlight(selectedColor);
            // Don't restore selection after highlighting
        }
    };

    const handleStrikethroughPdf = () => {
        console.log('Strikethrough button clicked');
        // Save selection before processing
        saveSelection();

        // Try question text first if it has a selection
        if (questionTextRef.current && questionTextRef.current.hasSelection()) {
            console.log('Question text has selection, applying strikethrough');
            questionTextRef.current.triggerStrikethrough();
            setTimeout(restoreSelection, 100);
            return;
        }

        const pdfRef = getActivePdfRef();
        if (pdfRef && pdfRef.hasSelection()) {
            pdfRef.triggerStrikethrough();
            setTimeout(restoreSelection, 100);
            return;
        }

        // If no selection found, try question text anyway
        if (questionTextRef.current) {
            questionTextRef.current.triggerStrikethrough();
            setTimeout(restoreSelection, 100);
        }
    };

    // Define highlight colors
    const highlightColors = [
        { key: 'yellow', color: '#ffff00', label: 'Yellow' },
        { key: 'green', color: '#00ff00', label: 'Green' },
        { key: 'pink', color: '#ffb3ba', label: 'Pink' },
        { key: 'blue', color: '#add8e6', label: 'Blue' }
    ];


    if (loading || !Q) return <Segment basic style={{ height: '40vh', ...noExtraSpaces }} loading={loading} />;
    if (error) return <ErrorSegment />;

    const QuestionContent = JSON.parse(Q?.contentJson);
    const StudentAnswers = JSON.parse(Q?.answerJson || '{}');
    const { worthDegree, correctorCommentTxt } = Q?.correctAnswer || {};
    const hasWorthDegree = worthDegree !== null && worthDegree !== undefined;
    const hasCorrectorComment = String(correctorCommentTxt ?? '').trim().length > 0;

    const onUserAnswerFile = (file, answer) => {
        const existing = JSON.parse(latestAnswerJsonRef.current);
        const merged = { ...existing, [file.__KEY__]: answer };
        latestAnswerJsonRef.current = JSON.stringify(merged);
        onAnswerQuestion({ ...QRef.current, answerJson: latestAnswerJsonRef.current });
    };

    return (
        <div style={{ backgroundColor: "#dcdddd", display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%', marginBottom: '5vh' }}>

            <div className='tw-flex tw-px-3 tw-py-1 tw-gap-5 tw-flex-row tw-items-center tw-bg-[#f2f2f2] tw-border-b'>

                <div onClick={() => handleOpenFreeModal('SYMBOL')} className='tw-font-bold tw-flex tw-items-center tw-flex-row tw-gap-3 tw-cursor-pointer hover:tw-text-red-500'>
                    <div><Icon name='dollar' /></div>
                    <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.symbol')}</p>
                </div>

                {/* Highlight Button with Color Picker */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {/* Color picker - simple button with popup */}
                    <Popup
                        trigger={
                            <div
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: highlightMode === 'remove' ? '#f0f0f0' : (highlightColors.find(c => c.key === highlightColor)?.color || '#ffff00'),
                                    border: '2px solid ' + (highlightMode === 'remove' ? '#ff0000' : '#ccc'),
                                    cursor: 'pointer'
                                }}
                                onClick={saveSelection}
                            />
                        }
                        content={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '10px' }} className='tw-font-bold'>
                                {highlightColors.map(color => {
                                    const isSelected = highlightMode === 'add' && highlightColor === color.key;
                                    return (
                                        <div
                                            key={color.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                cursor: 'pointer',
                                                padding: '5px',
                                                borderRadius: '3px',
                                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                                border: isSelected ? '1px solid #2196f3' : '1px solid transparent'
                                            }}
                                            onClick={() => {
                                                setHighlightColor(color.key);
                                                setHighlightMode('add');
                                                // Don't apply immediately when selecting color, just set the mode
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) e.target.style.backgroundColor = '#f0f0f0';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) e.target.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: color.color,
                                                    border: isSelected ? '2px solid #2196f3' : '1px solid #ccc'
                                                }}
                                            />
                                            <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>{color.label}</span>
                                        </div>
                                    );
                                })}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        padding: '5px',
                                        borderRadius: '3px',
                                        borderTop: '1px solid #ccc',
                                        marginTop: '5px',
                                        paddingTop: '10px',
                                        backgroundColor: highlightMode === 'remove' ? '#ffebee' : 'transparent',
                                        border: highlightMode === 'remove' ? '1px solid #f44336' : '1px solid transparent'
                                    }}
                                    onClick={() => {
                                        setHighlightMode('remove');
                                        console.log('Remove mode selected - waiting for button click');
                                    }}
                                    onMouseEnter={(e) => {
                                        if (highlightMode !== 'remove') e.target.style.backgroundColor = '#f0f0f0';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (highlightMode !== 'remove') e.target.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor: '#f0f0f0',
                                            border: highlightMode === 'remove' ? '2px solid #f44336' : '1px solid #ccc'
                                        }}
                                    />
                                    <span style={{ fontWeight: highlightMode === 'remove' ? 'bold' : 'normal', fontFamily: "Arial" }}>Remove Highlight</span>
                                </div>
                            </div>
                        }
                        on='click'
                        position='bottom left'
                    />
                    <div
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            saveSelection();
                            handleHighlightPdf(); // Don't pass color, let it use current mode
                        }} className='tw-font-bold tw-flex tw-items-center tw-flex-row tw-gap-3 tw-cursor-pointer hover:tw-text-red-500'>
                        <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.highlight')}</p>
                    </div>
                </div>

                <div
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        saveSelection();
                        handleStrikethroughPdf();
                    }}
                    className='tw-font-bold tw-flex tw-items-center tw-flex-row  tw-cursor-pointer hover:tw-text-red-500'>
                    <div><MdFormatStrikethrough size={20} /></div>
                    <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.strikethrough')}</p>
                </div>

                <div onClick={() => setShowCalculator(true)} className='tw-font-bold tw-flex tw-items-center tw-flex-row tw-gap-3 tw-cursor-pointer hover:tw-text-red-500'>
                    <div><Icon name='calculator' /></div>
                    <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.calculator')}</p>
                </div>

                <div onClick={() => handleOpenFreeModal('SCRATCHPAD')} className='tw-font-bold tw-flex tw-items-center tw-flex-row tw-gap-3 tw-cursor-pointer hover:tw-text-red-500'>
                    <div><Icon name='pencil' /></div>
                    <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.scratchPad')}</p>
                </div>

                <div>
                    {Q?.sourceTxt && <Header as='h5' style={noExtraSpaces}>{Q.sourceTxt}</Header>}
                </div>

                <div className='tw-flex tw-flex-row tw-items-center tw-gap-5 tw-ms-auto'>
                    {Object.keys(freeModals).length > 0 && <div onClick={() => setFreeModals({})} className='tw-font-bold tw-flex tw-flex-row tw-items-center tw-gap-3 hover:tw-text-red-500 tw-cursor-pointer'>
                        <div><Icon name='window close outline' /></div>
                        <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.closeAll')}</p>
                    </div>}

                    <div onClick={() => onFlagQuestion(Q)} className='tw-font-bold tw-flex tw-flex-row tw-items-center tw-gap-3 hover:tw-text-red-500 tw-cursor-pointer'>
                        <div><Icon name={Q.isFlagged ? "flag" : "flag outline"} /></div>
                        <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.flag')}</p>
                    </div>

                    {!isMockExam && <div onClick={() => onFavQuestion(Q)} className='tw-font-bold tw-flex tw-flex-row tw-items-center tw-gap-3 hover:tw-text-red-500 tw-cursor-pointer'>
                        <div><Icon name={Q.isFav ? "star" : "star outline"} /></div>
                        <p style={{ fontFamily: "Arial" }}>{t('pageDifr.toolbar.favorite')}</p>
                    </div>}
                </div>
            </div>

            <PanelGroup direction="horizontal" autoSaveId="persistence">
                <Panel defaultSize={25} className='tw-bg-white tw-border tw-border-[#bbb]'>
                    <div className='page-difr-sidebar tw-p-4 !tw-pe-[3px] tw-overflow-auto tw-h-[77.8vh]'>
                        <div onMouseDown={() => setActiveModalKey(null)}>
                            <Header style={{ fontFamily: "Arial" }}>{t('pageDifr.sidebar.exhibits')}</Header>
                            {
                                Q.attachments?.map((d, i) => <div className='tw-group' key={i} onClick={() => handleOpenFreeModal(getAttachmentKey(i, Q?.userQuestionRoundId))} style={{ cursor: "pointer", display: d.type === "CORRECT_ANSWER" ? 'none' : 'flex', flexDirection: 'row', alignItems: 'center', gap: 5, padding: 10, border: 'solid #ccc 1px', marginBottom: 5, backgroundColor: '#f2f2f2' }}>
                                    <div><Icon link color='black' name="book" /></div>
                                    <div><Header className='group-hover:tw-text-red-500' style={sidebarItemTextStyle}>{isArabic ? d.nameAr : d.nameEn}</Header></div>
                                </div>)
                            }
                        </div>

                        {((!isMockExam && Object.keys(StudentAnswers).length) || isViewOnly) && <div onMouseDown={() => setActiveModalKey(null)}>
                            <Header style={{ fontFamily: "Arial" }}>{t('pageDifr.sidebar.correctAnswer')}</Header>

                            <div className='tw-group' onClick={() => setShowExplanation(true)} style={{ cursor: "pointer", display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5, padding: 10, border: 'solid #ccc 1px', marginBottom: 5, backgroundColor: '#f2f2f2' }}>
                                <div><Icon link color='black' name="video play" /></div>
                                <div><Header className='group-hover:tw-text-red-500' style={sidebarItemTextStyle}>{t("pageDifr.sidebar.explanation")}</Header></div>
                            </div>

                            {
                                Q.attachments?.map((d, i) => <div className='tw-group' key={i} onClick={() => handleOpenFreeModal(getAttachmentKey(i, Q?.userQuestionRoundId))} style={{ cursor: "pointer", display: d.type === "CORRECT_ANSWER" ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 5, padding: 10, border: 'solid #ccc 1px', marginBottom: 5, backgroundColor: '#f2f2f2' }}>
                                    <div><Icon link color='black' name="book" /></div>
                                    <div><Header className='group-hover:tw-text-red-500' style={sidebarItemTextStyle}>{isArabic ? d.nameAr : d.nameEn}</Header></div>
                                </div>)
                            }
                        </div>}

                        {isViewOnly && (hasWorthDegree || hasCorrectorComment) && <div onMouseDown={() => setActiveModalKey(null)}>
                            <Segment style={{ marginBottom: 15, fontFamily: "Arial" }}>
                                {hasWorthDegree && <div>
                                    <Header as='h4' color='red' style={{ fontFamily: "Arial" }}>{t('Degree')}</Header>
                                    <span>{worthDegree}</span>
                                </div>}
                                {hasWorthDegree && hasCorrectorComment && <div className='tw-my-3 tw-border-t tw-border-[#ddd]' />}
                                {hasCorrectorComment && <div>
                                    <Header as='h4' color='red' style={{ fontFamily: "Arial" }}>{t('CorrectorComment')}</Header>
                                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {correctorCommentTxt}
                                    </p>
                                </div>}
                            </Segment>
                        </div>}

                        <div onMouseDown={() => setActiveModalKey(null)}>
                            <Header style={{ fontFamily: "Arial" }}>{t('pageDifr.sidebar.requirements')}</Header>
                            {
                                QuestionContent?.requirements.map((r, i) => <div className='tw-group' key={i} onClick={() => handleOpenFreeModal(r.__KEY__)} style={{ cursor: "pointer", display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 5, padding: 10, border: 'solid #ccc 1px', marginBottom: 5, backgroundColor: '#f2f2f2' }}>
                                    <div style={{ display: "flex", alignItems: 'center', flexDirection: "row", gap: 5 }}>
                                        <div><Icon link color='black' name="target" /></div>
                                        <div><Header className='group-hover:tw-text-red-500' style={sidebarItemTextStyle}>{`${r.title} (${r.mark} ${t('pageDifr.sidebar.marks')})`}</Header></div>
                                    </div>

                                    {((!isMockExam && Object.keys(StudentAnswers).length) || isViewOnly) ? <div style={{ display: "flex", alignItems: 'center', flexDirection: "row", gap: 2 }}>
                                        {r?.attachments?.map((at, indx) => <Icon link
                                            key={indx}
                                            name={at.type === 'VIDEO' ? "video" : 'file alternate'}
                                            size='small'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                at.type === 'FILE' && handleOpenFreeModal(at.docUrl);
                                                at.type === 'VIDEO' && setShowAttachmentVideo(at.docUrl);
                                            }}
                                        />)}
                                    </div> : null}
                                </div>)
                            }
                        </div>

                        <div onMouseDown={() => setActiveModalKey(null)}>
                            <Header style={{ fontFamily: "Arial" }}>{t('pageDifr.sidebar.responseOptions')}</Header>
                            {
                                QuestionContent.files.map((f, index) => <div className='tw-group' onClick={() => handleOpenFreeModal(f.__KEY__)} key={index} style={{ cursor: "pointer", display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5, padding: 10, border: 'solid #ccc 1px', marginBottom: 5, backgroundColor: '#f2f2f2' }}>
                                    <div><Icon link color='black' name={f.fileType === 'WORD' ? "file text outline" : "table"} /></div>
                                    <div><Header className='group-hover:tw-text-red-500' style={sidebarItemTextStyle}>{f.fileType === "WORD" ? t('pageDifr.sidebar.wordProcessor') : t('pageDifr.sidebar.spreadsheet')}</Header></div>
                                </div>)
                            }
                        </div>
                    </div>
                </Panel>

                <PanelResizeHandle className="!tw-w-[7px] !tw-bg-[#DCDDDD] difr-resize-custom-class"
                    style={{ cursor: "ew-resize !important" }} />

                <Panel defaultSize={75} className='tw-bg-white tw-border tw-border-[#bbb]'>

                    <div onMouseDown={() => setActiveModalKey(null)} style={{ position: 'relative', backgroundColor: 'white', padding: 10 }}>
                        <QuestionTextViewer
                            ref={questionTextRef}
                            questionId={Q?.userQuestionRoundId}
                            className='page-difr-question-text'
                            htmlContent={replaceFontFamily(isArabic ? QuestionContent?.textAr?.trim() : QuestionContent?.textEn?.trim(), "Arial", null, true)}
                        />

                        {/* <div className='studentExamWatermark' style={{ top: '40%', left: '40%' }}>
                            <Image loading="lazy" size='medium' src={iconImage} />
                        </div> */}

                        <Header
                            as='h1'
                            content={`${user.firstName} ${user.lastName} [${user.email}] ${user.contactNumber}`}
                            className='studentExamWatermark'
                        />

                    </div>
                </Panel>
            </PanelGroup>

            <Modal
                onClose={() => setShowReportIssue(false)}
                open={showReportIssue}
                content={<ReportForm reportOn={Consts.REPORTS_TYPES.QUESTION} reportLink={Q} id={Q.questionId} showModal={setShowReportIssue} />}
                closeIcon
                header={t('pageDifr.modals.reportProblem')}
                size='small'
                centered
                closeOnDimmerClick={false}
            />

            <Modal
                onClose={() => setShowExplanation(false)}
                open={showExplanation}
                content={Q && Q.correctAnswer && Q.correctAnswer.explanations && Q.correctAnswer.explanations.videos.length ? <VideoPlayer kind={Consts.VIDEOS_KINDS.EXPLANATION} videoDetails={{ ...Q.correctAnswer.explanations.videos[0], ...Q }} onClose={() => setShowExplanation(false)} /> : <ErrorSegment text={t('pageDifr.modals.comingSoon')} icon='wait' />}
                basic={Q && Q.correctAnswer && Q.correctAnswer.explanations && Q.correctAnswer.explanations.videos.length ? true : false}
                size='large'
                centered
                closeIcon={!(Q && Q.correctAnswer && Q.correctAnswer.explanations && Q.correctAnswer.explanations.videos.length)}
                closeOnDimmerClick={false}
            />

            <Modal
                onClose={() => setShowAttachmentVideo(null)}
                open={Boolean(showAttachmentVideo)}
                content={Boolean(showAttachmentVideo) && <VideoPlayer kind={Consts.VIDEOS_KINDS.GENERAL} videoDetails={{ url: showAttachmentVideo.urlEn, videoId: showAttachmentVideo.videoId }} onClose={() => setShowAttachmentVideo(null)} />}
                size='small'
                centered
                closeIcon
                closeOnDimmerClick={false}
            />


            <FreeModal
                onClose={setFreeModals}
                fileKey={'SCRATCHPAD'}
                title={t('pageDifr.modals.scratchPad')}
                open={freeModals['SCRATCHPAD']}
                onMouseDown={() => bringToFront('SCRATCHPAD')}
                children={<TextEditor data={{ value: scratchPad, setValue: setScratchPad }} />}
            />

            {showCalculator && <FlexWindow
                isVisible={showCalculator}
                onClose={() => setShowCalculator(false)}
                title={t("pageDifr.modals.calculator")}
            >
                <Calculator />
            </FlexWindow>}

            {
                Q.attachments.map((d, i) => <FreeModal
                    key={i}
                    onMouseDown={() => bringToFront(getAttachmentKey(i, Q?.userQuestionRoundId))}
                    onClose={setFreeModals}
                    fileKey={getAttachmentKey(i, Q?.userQuestionRoundId)}
                    title={isArabic ? d.nameAr : d.nameEn}
                    open={freeModals[getAttachmentKey(i, Q?.userQuestionRoundId)]}
                    children={
                        d.fsType === "FR_EXCEL" ?
                            <SpreadsheetEditor docUrl={d.docUrl} readOnly /> :
                            d.fsType === "FR_WORD" ?
                                <TextEditor docUrl={d.docUrl} data={{ value: "" }} isReadOnly /> :
                                <TbsPdfReader
                                    ref={(el) => { if (el) pdfReaderRefs.current[getAttachmentKey(i, Q?.userQuestionRoundId)] = el; }}
                                    docUrl={d.docUrl}
                                    instanceId={getAttachmentKey(i, Q?.userQuestionRoundId)}
                                />}
                />)
            }

            {
                QuestionContent.requirements.map((r, i) => <FreeModal
                    key={i}
                    onClose={setFreeModals}
                    fileKey={r.__KEY__}
                    title={r?.title?.toUpperCase()}
                    open={freeModals[r.__KEY__]}
                    onMouseDown={() => bringToFront(r.__KEY__)}
                    children={<HtmlEditor value={isArabic ? r?.contentAr : r?.contentEn} readOnly style={{ border: 'none', boxShadow: 'none' }} />}
                />
                )
            }

            {
                QuestionContent.requirements
                    .reduce((acc, r) => [...acc, ...r?.attachments?.filter(at => at.type === "FILE") || []], [])
                    .map((at, i) => <FreeModal
                        key={i}
                        onClose={setFreeModals}
                        fileKey={at.docUrl}
                        title={t('pageDifr.modals.answerOfRequirement')}
                        open={freeModals[at.docUrl]}
                        onMouseDown={() => bringToFront(at.docUrl)}
                        children={
                            <TbsPdfReader docUrl={at.docUrl} />
                        }
                    />)
            }

            {
                QuestionContent.files.map((f, i) => <FreeModal
                    onClose={setFreeModals}
                    fileKey={f.__KEY__}
                    title={f.fileType === 'EXCEL' ? t("pageDifr.modals.spreadsheet") : t("pageDifr.modals.wordProcessor")}
                    open={freeModals[f.__KEY__]}
                    children={<FileAnswer isViewOnly={isViewOnly} file={f} onAnswer={onUserAnswerFile} answer={StudentAnswers[f.__KEY__]} />}
                    key={i}
                    onMouseDown={() => bringToFront(f.__KEY__)}
                />)
            }

            <FreeModal
                onClose={setFreeModals}
                fileKey={'SYMBOL'}
                title={t('pageDifr.modals.symbol')}
                open={freeModals['SYMBOL']}
                children={<Symbols onClose={setFreeModals} addTextToActiveElement={addTextToActiveElement} />}
            />

        </div >
    );
};


const FileAnswer = ({ answer, onAnswer, file, isViewOnly }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const initialAdminValue = isArabic
        ? file?.fileContentAr
        : file?.fileContentEn;

    const [value, setValue] = useState(answer || initialAdminValue || "");
    const valueRef = useRef(value);
    const [resetKey, setResetKey] = useState(0);

    const onAnswerRef = useRef(onAnswer);
    useEffect(() => { onAnswerRef.current = onAnswer; }, [onAnswer]);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    useEffect(() => {
        return () => {
            if (!isViewOnly && valueRef.current) {
                onAnswerRef.current(file, valueRef.current);
            }
        };
    }, []);

    // Callback to trigger full remount after reset
    const handleResetComplete = () => {
        setResetKey((prev) => prev + 1); // Forces unmount + fresh mount
    };

    return file.fileType === "EXCEL" ? (
        <SpreadsheetEditorNew
            key={resetKey}
            data={{ value, setValue }}
            hideAction
            // onBlur={() => isViewOnly ? undefined : onAnswer(file, value)}
            readOnly={isViewOnly}
            onResetComplete={handleResetComplete}
        />
    ) : (
        <TextEditorTinyMC
            onBlur={() => (isViewOnly ? undefined : onAnswer(file, value))}
            data={{ value, setValue }}
            isArabic={isArabic}
            isReadOnly={isViewOnly}
        />
    );
};

const Symbols = ({ addTextToActiveElement }) => {
    const onSelect = (sym) => {
        addTextToActiveElement(sym);
        // navigator.clipboard.writeText(sym);
    };
    return (
        <div>
            <div className='tw-flex tw-flex-row tw-items-center'>
                <div onClick={() => onSelect("$")} className='tw-border tw-cursor-pointer tw-p-5 hover:tw-bg-gray-200'>$</div>
                <div onClick={() => onSelect("€")} className='tw-border tw-cursor-pointer tw-p-5 hover:tw-bg-gray-200'>€</div>
                <div onClick={() => onSelect("£")} className='tw-border tw-cursor-pointer tw-p-5 hover:tw-bg-gray-200'>£</div>
                <div onClick={() => onSelect("¥")} className='tw-border tw-cursor-pointer tw-p-5 hover:tw-bg-gray-200'>¥</div>
                <div onClick={() => onSelect("₽")} className='tw-border tw-cursor-pointer tw-p-5 hover:tw-bg-gray-200'>₽</div>
            </div>
        </div>
    );
};

export default QuestionPage;
